import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQuery } from './baseQuery'
import type { DeliverableItem } from '@adthlete/shared-frontend'

export type { DeliverableItem } from '@adthlete/shared-frontend'

export interface CollaborationEvent {
  id: string
  title: string
  description: string | null
  location: string | null
  startAt: string
  endAt: string | null
  status: 'scheduled' | 'cancelled'
  createdAt: string
  linkId?: string
  rsvpStatus: 'pending' | 'accepted' | 'declined' | 'proposed_changes'
  athleteNote: string | null
  proposedStartAt: string | null
  proposedEndAt: string | null
  rsvpRespondedAt: string | null
}

export interface Brand {
  id?: string
  name: string
  logo: string
}

export interface MilestoneCheckIn {
  active: boolean
  response: 'on_track' | 'needs_more_time' | null
  responseAt: string | null
}

export interface MilestoneDateProposal {
  id: string
  proposedDueDate: string
  reason: string | null
  status: 'open' | 'accepted' | 'declined' | 'cancelled' | 'expired'
  responseNote: string | null
  createdAt: string
  respondedAt: string | null
  expiresAt: string
}

export interface Milestone {
  id: number
  name: string
  slug?: string
  description: string
  sequenceOrder: number
  dueDate: string
  status: string // 'pending', 'submitted', 'approved'
  requiresApproval: boolean
  approvedSubmissionId?: number
  metadata?: any
  completedAt?: string
  createdAt: string
  updatedAt: string
  submissions?: any[]
  checkIn?: MilestoneCheckIn | null
  dateProposal?: MilestoneDateProposal | null
}

export interface Collaboration {
  id: string
  waveId?: string
  title: string
  platform: string[]
  paymentAmount: number
  paymentType?: 'fixed' | 'custom'
  customPaymentTerms?: string | null
  status: string // 'offered', 'accepted', 'rejected', etc.
  waveStatus?: string
  brief: string
  instructions?: string
  script?: string | null
  audioUrl?: string | null
  imageUrl?: string | null
  postLinks?: string[]
  contentCreatedAt?: string | null
  contentApprovedAt?: string | null
  contentApprovedUrl?: string | null
  invitationLink?: string | null
  brand: Brand
  brandId?: string
  bidReceivedAt?: string
  dueDate?: string
  startDate?: string
  endDate?: string
  contentDueDate?: string
  metrics?: any
  paymentStatus?: string
  paymentDate?: string | null
  notificationShown?: boolean
  dueDateNotificationShown?: boolean
  complianceNeeded?: boolean
  conceptStatus?: string // 'submitted', 'approved', 'changes_requested'
  videoReviewStatus?: string // 'submitted', 'approved', 'changes_requested'
  finalVideoStatus?: string // 'pending', 'posted'
  submittedAt?: string
  approvedAt?: string
  milestones?: Milestone[]
  organization?: {
    name: string
    logo: string
  }
  brandManager?: {
    name: string
    email: string
    avatar: string
  }
  athlete?: {
    name: string
    email: string
    avatar: string
  }
  legacyContentSubmission?: any
  structuredDeliverables?: DeliverableItem[] | null
  // Compliance fields
  dealExecutedAt?: string | null
  payout?: number | null
  contractUrl?: string | null
  nilGoStatus?: string
  nilGoConfirmationNumber?: string | null
  nilGoSubmittedAt?: string | null
  nilGoFields?: Record<string, string | null> | null
  institutionalDisclosureStatus?: string
  institutionalDisclosureSubmittedAt?: string | null
  contractScanResult?: {
    overallResult: 'clean' | 'flagged' | 'not_scanned'
    scannedAt?: string | null
    flags?: Array<{
      id: string
      category: string
      severity: 'critical' | 'warning' | 'info'
      excerpt: string
      rule: string
      recommendation: string
    }>
  } | null
  timeline?: Array<{
    id: number
    body: string
    messageType: string
    createdAt: string
    metadata?: Record<string, unknown>
    milestone?: {
      id: number
      name: string
    } | null
    author?: {
      id: string
      name: string
      avatar: string | null
    } | null
  }>
}

export const collaborationsApi = createApi({
  reducerPath: 'campaignsApi',
  baseQuery,
  tagTypes: ['Collaboration', 'CollaborationDetail'],
  endpoints: (builder) => ({
    getCampaigns: builder.query<Collaboration[], void>({
      query: () => '/athlete/collaborations',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Collaboration' as const, id })),
              { type: 'Collaboration', id: 'LIST' },
            ]
          : [{ type: 'Collaboration', id: 'LIST' }],
    }),
    getCampaignDetail: builder.query<Collaboration, string>({
      query: (campaignId) => `/athlete/collaborations/${campaignId}`,
      providesTags: (result, error, id) => [{ type: 'CollaborationDetail', id }],
    }),
    acceptCampaign: builder.mutation<void, string>({
      query: (campaignId) => ({
        url: `/athlete/collaborations/${campaignId}`,
        method: 'PATCH',
        body: { status: 'accepted' },
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Collaboration', id },
        { type: 'Collaboration', id: 'LIST' },
        { type: 'CollaborationDetail', id },
      ],
    }),
    rejectCampaign: builder.mutation<void, string>({
      query: (campaignId) => ({
        url: `/athlete/collaborations/${campaignId}`,
        method: 'PATCH',
        body: { status: 'rejected' },
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Collaboration', id },
        { type: 'Collaboration', id: 'LIST' },
        { type: 'CollaborationDetail', id },
      ],
    }),
    restoreCampaign: builder.mutation<void, string>({
      query: (campaignId) => ({
        url: `/athlete/collaborations/${campaignId}`,
        method: 'PATCH',
        body: { status: 'offered' },
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Collaboration', id },
        { type: 'Collaboration', id: 'LIST' },
        { type: 'CollaborationDetail', id },
      ],
    }),
    updateMilestoneMetadata: builder.mutation<
      { campaignId: string; milestoneId: number; metadata: any },
      { campaignId: string; milestoneId: number; metadata: any }
    >({
      query: ({ campaignId, milestoneId, metadata }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/metadata`,
        method: 'PATCH',
        body: { metadata },
      }),
      invalidatesTags: ['CollaborationDetail'],
    }),
    submitMilestone: builder.mutation<
      any,
      { campaignId: string; milestoneId: number; submissionData: any }
    >({
      query: ({ campaignId, milestoneId, submissionData }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/submissions`,
        method: 'POST',
        body: submissionData,
      }),
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: 'Collaboration', id: 'LIST' },
        { type: 'Collaboration', id: campaignId },
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    deleteMilestoneSubmission: builder.mutation<
      any,
      { campaignId: string; milestoneId: number; submissionId: number }
    >({
      query: ({ campaignId, milestoneId, submissionId }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/submissions/${submissionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CollaborationDetail'],
    }),
    submitMilestoneCheckin: builder.mutation<
      { id: number; milestoneId: string; response: string; responseAt: string },
      { campaignId: string; milestoneId: string; response: 'on_track' | 'needs_more_time' }
    >({
      query: ({ campaignId, milestoneId, response }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/checkin`,
        method: 'POST',
        body: { response },
      }),
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    createMilestoneDateProposal: builder.mutation<
      MilestoneDateProposal,
      {
        campaignId: string
        milestoneId: string
        proposedDueDate: string
        reason?: string | null
      }
    >({
      query: ({ campaignId, milestoneId, proposedDueDate, reason }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/date-proposals`,
        method: 'POST',
        body: { proposedDueDate, reason: reason ?? null },
      }),
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    cancelMilestoneDateProposal: builder.mutation<
      MilestoneDateProposal,
      { campaignId: string; milestoneId: string; proposalId: string }
    >({
      query: ({ campaignId, milestoneId, proposalId }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/date-proposals/${proposalId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    getSubmissionTimeline: builder.query<
      any,
      { campaignId: string; milestoneId: number; submissionId: number }
    >({
      query: ({ campaignId, milestoneId, submissionId }) =>
        `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/submissions/${submissionId}/timeline`,
      providesTags: (result, error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    addSubmissionComment: builder.mutation<
      any,
      {
        campaignId: string
        milestoneId: number
        submissionId: number
        text: string
        commentType?: string
        parentCommentId?: number
        metadata?: any
      }
    >({
      query: ({
        campaignId,
        milestoneId,
        submissionId,
        text,
        commentType,
        parentCommentId,
        metadata,
      }) => ({
        url: `/athlete/collaborations/${campaignId}/milestones/${milestoneId}/submissions/${submissionId}/comments`,
        method: 'POST',
        body: { text, commentType, parentCommentId, metadata },
      }),
      // Invalidate the specific timeline query if possible, or just CollaborationDetail to be safe/lazy
      // Ideally we would have a 'Timeline' tag
      invalidatesTags: ['CollaborationDetail'],
    }),
    createStandaloneCollaboration: builder.mutation<Collaboration, any>({
      query: (data) => ({
        url: '/athlete/collaborations/standalone',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Collaboration', id: 'LIST' }],
    }),
    inviteBrand: builder.mutation<
      void,
      { campaignId: string; email: string; brandName?: string; message?: string }
    >({
      query: ({ campaignId, email, brandName, message }) => ({
        url: `/athlete/collaborations/${campaignId}/invite`,
        method: 'POST',
        body: { email, brandName, message },
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: 'Collaboration', id: campaignId },
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    getPendingInvitations: builder.query<any[], void>({
      query: () => '/athlete/collaborations/invitations/pending',
      providesTags: ['Collaboration'],
    }),
    getCreativeIdeas: builder.query<
      {
        ideas: Array<{
          id: string
          title: string
          content: {
            concept?: string
            theme?: string
            integration_concept?: string
            talking_points?: string
          }
          createdBy: string
          createdByTeam?: string | null
          createdAt: string
        }>
      },
      string
    >({
      query: (campaignId) => `/athlete/collaborations/${campaignId}/creative-ideas`,
    }),
    sendCaptionChat: builder.mutation<
      { reply: string; suggestedCaption: string | null },
      { campaignId: string; messages: Array<{ role: 'user' | 'assistant'; content: string }> }
    >({
      query: ({ campaignId, messages }) => ({
        url: `/athlete/collaborations/${campaignId}/caption-chat`,
        method: 'POST',
        body: { messages },
      }),
    }),
    getVideoSuggestions: builder.query<
      {
        suggestions: Array<{
          id: string
          type: string
          videoUrl?: string
          filename?: string
          fileSize?: number
          contentType?: string
          caption?: string
          deliverableId?: string
          sameAsDeliverableId?: string
          createdAt: string
        }>
      },
      string
    >({
      query: (campaignId) => `/athlete/collaborations/${campaignId}/video-suggestions`,
    }),
    sendCollaborationMessage: builder.mutation<any, { campaignId: string; message: string }>({
      query: ({ campaignId, message }) => ({
        url: `/athlete/collaborations/${campaignId}/messages`,
        method: 'POST',
        body: { message },
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    updateCollaboration: builder.mutation<
      void,
      {
        campaignId: string
        updates: {
          nilGoStatus?: string
          nilGoConfirmationNumber?: string | null
          nilGoSubmittedAt?: string | null
          nilGoFields?: Record<string, string | null> | null
          institutionalDisclosureStatus?: string
          institutionalDisclosureSubmittedAt?: string | null
        }
      }
    >({
      query: ({ campaignId, updates }) => ({
        url: `/athlete/collaborations/${campaignId}`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    updateDeliverables: builder.mutation<
      { structuredDeliverables: import('@adthlete/shared-frontend').DeliverableItem[] },
      { campaignId: string; deliverables: import('@adthlete/shared-frontend').DeliverableItem[] }
    >({
      query: ({ campaignId, deliverables }) => ({
        url: `/athlete/collaborations/${campaignId}/deliverables`,
        method: 'PATCH',
        body: { deliverables },
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    scanContract: builder.mutation<
      {
        overallResult: 'clean' | 'flagged' | 'not_scanned'
        contractAvailable: boolean
        scannedAt?: string
        flags?: any[]
      },
      string
    >({
      query: (campaignId) => ({
        url: `/athlete/campaigns/${campaignId}/compliance/scan`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, campaignId) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    getCalendarEvents: builder.query<
      { connected: boolean; events: any[] },
      { start: string; end: string }
    >({
      query: ({ start, end }) =>
        `/athlete/calendar/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    }),
    getMilestoneEvents: builder.query<{ events: any[] }, { start: string; end: string }>({
      query: ({ start, end }) =>
        `/athlete/calendar/milestone-events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    }),
    getCollaborationEvents: builder.query<CollaborationEvent[], string>({
      query: (campaignId) => `/athlete/collaborations/${campaignId}/events`,
      providesTags: (result, error, campaignId) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    rsvpCollaborationEvent: builder.mutation<
      CollaborationEvent,
      {
        campaignId: string
        eventId: string
        rsvpStatus: 'accepted' | 'declined' | 'proposed_changes'
        athleteNote?: string
        proposedStartAt?: string
        proposedEndAt?: string
      }
    >({
      query: ({
        campaignId,
        eventId,
        rsvpStatus,
        athleteNote,
        proposedStartAt,
        proposedEndAt,
      }) => ({
        url: `/athlete/collaborations/${campaignId}/events/${eventId}/rsvp`,
        method: 'PATCH',
        body: { rsvpStatus, athleteNote, proposedStartAt, proposedEndAt },
      }),
      invalidatesTags: (result, error, { campaignId }) => [
        { type: 'CollaborationDetail', id: campaignId },
      ],
    }),
    getCollaborationCalendarEvents: builder.query<
      { events: any[] },
      { start: string; end: string }
    >({
      query: ({ start, end }) =>
        `/athlete/calendar/collaboration-events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    }),
  }),
})

export const {
  useGetCampaignsQuery,
  useGetCampaignDetailQuery,
  useAcceptCampaignMutation,
  useRejectCampaignMutation,
  useRestoreCampaignMutation,
  useUpdateMilestoneMetadataMutation,
  useSubmitMilestoneMutation,
  useSubmitMilestoneCheckinMutation,
  useCreateMilestoneDateProposalMutation,
  useCancelMilestoneDateProposalMutation,
  useDeleteMilestoneSubmissionMutation,
  useGetSubmissionTimelineQuery,
  useAddSubmissionCommentMutation,
  useCreateStandaloneCollaborationMutation,
  useInviteBrandMutation,
  useGetPendingInvitationsQuery,
  useGetCreativeIdeasQuery,
  useSendCaptionChatMutation,
  useGetVideoSuggestionsQuery,
  useSendCollaborationMessageMutation,
  useUpdateCollaborationMutation,
  useUpdateDeliverablesMutation,
  useScanContractMutation,
  useGetCalendarEventsQuery,
  useGetMilestoneEventsQuery,
  useGetCollaborationEventsQuery,
  useRsvpCollaborationEventMutation,
  useGetCollaborationCalendarEventsQuery,
} = collaborationsApi
